import type { GeometryElement } from 'jsxgraph';

/**
 * Resolved target information from a path.
 */
interface ResolvedTarget {
  /** The element ID (from config params) */
  elementId: string;
  /** Specific index within the element's objects */
  index?: number | '*';
  /** Whether this targets multiple objects */
  isMultiple: boolean;
}

/**
 * Result of target resolution.
 */
export interface TargetResolutionResult {
  /** Parsed target info */
  target: ResolvedTarget;
  /** Matching JSXGraph object IDs */
  objectIds: string[];
  /** Matching JSXGraph objects */
  objects: GeometryElement[];
}

/**
 * Resolve a target path to JSXGraph objects.
 *
 * Target path formats:
 * - "f"           → All objects for element "f" (f-0, f-1, etc.)
 * - "f.0"         → Single object f-0
 * - "f[0]"        → Single object f-0 (bracket notation)
 * - "f-roots"     → All objects for f-roots (f-roots-0, f-roots-1)
 * - "f-roots.1"   → Single object f-roots-1
 * - "f-roots[*]"  → Explicit wildcard for all roots
 *
 * @param targetPath - Path like "f", "f.0", "f-roots", "f-roots[*]"
 * @param objectsMap - The widget's objects Map<string, GeometryElement>
 */
export function resolveTarget(targetPath: string, objectsMap: Map<string, GeometryElement>): TargetResolutionResult {
  const parsed = parseTargetPath(targetPath);
  const matchingIds: string[] = [];
  const matchingObjects: GeometryElement[] = [];

  objectsMap.forEach((obj, id) => {
    if (matchesTarget(id, parsed)) {
      matchingIds.push(id);
      matchingObjects.push(obj);
    }
  });

  return {
    target: parsed,
    objectIds: matchingIds,
    objects: matchingObjects,
  };
}

/**
 * Parse a target path into structured components.
 */
function parseTargetPath(path: string): ResolvedTarget {
  // Handle bracket notation: "f-roots[0]" → "f-roots.0"
  const normalized = path.replace(/\[(\d+|\*)\]/g, '.$1');

  // Find the last segment that could be an index
  const lastDotIndex = normalized.lastIndexOf('.');
  let elementId: string;
  let index: number | '*' | undefined;
  let isMultiple = false;

  if (lastDotIndex === -1) {
    // No dot - entire path is the element ID
    elementId = normalized;
    isMultiple = true; // Target all objects for this element
  } else {
    const lastPart = normalized.slice(lastDotIndex + 1);

    if (lastPart === '*') {
      // Explicit wildcard
      elementId = normalized.slice(0, lastDotIndex);
      index = '*';
      isMultiple = true;
    } else if (/^\d+$/.test(lastPart)) {
      // Numeric index
      elementId = normalized.slice(0, lastDotIndex);
      index = parseInt(lastPart, 10);
      isMultiple = false;
    } else {
      // Not an index - entire path is element ID
      elementId = normalized;
      isMultiple = true;
    }
  }

  return { elementId, index, isMultiple };
}

/**
 * Check if a JSXGraph object ID matches a target.
 *
 * Object IDs follow pattern: "{elementId}-{index}"
 * Examples: "f-0", "f-roots-0", "f-roots-1", "x", "y"
 */
function matchesTarget(objectId: string, target: ResolvedTarget): boolean {
  // Exact match (for objects without index like axis IDs)
  if (objectId === target.elementId) {
    return target.index === undefined || target.index === '*';
  }

  // Check if objectId starts with elementId followed by dash
  if (!objectId.startsWith(`${target.elementId}-`)) {
    return false;
  }

  // Extract the suffix after elementId
  const suffix = objectId.slice(target.elementId.length + 1);

  // Suffix should be a number (the index)
  if (!/^\d+$/.test(suffix)) {
    return false;
  }

  const objIndex = parseInt(suffix, 10);

  // Check index match
  if (target.index === undefined || target.index === '*') {
    return true; // Match all indices
  }

  return objIndex === target.index;
}

/**
 * Compute bounding box for a set of JSXGraph objects.
 */
export function computeBoundingBoxForTargets(
  objects: GeometryElement[],
  padding = 2,
): [number, number, number, number] | null {
  if (objects.length === 0) {
    return null;
  }

  let minX = Infinity,
    maxX = -Infinity;
  let minY = Infinity,
    maxY = -Infinity;

  for (const obj of objects) {
    const bounds = getObjectBounds(obj);
    if (bounds) {
      minX = Math.min(minX, bounds.minX);
      maxX = Math.max(maxX, bounds.maxX);
      minY = Math.min(minY, bounds.minY);
      maxY = Math.max(maxY, bounds.maxY);
    }
  }

  // If we couldn't compute bounds, return null
  if (!isFinite(minX) || !isFinite(maxX) || !isFinite(minY) || !isFinite(maxY)) {
    return null;
  }

  // Apply padding
  const width = Math.max(maxX - minX, 1);
  const height = Math.max(maxY - minY, 1);
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  const halfW = width / 2 + padding;
  const halfH = height / 2 + padding;

  return [centerX - halfW, centerY + halfH, centerX + halfW, centerY - halfH];
}

interface ObjectBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

function getObjectBounds(obj: GeometryElement): ObjectBounds | null {
  // Points - check for X() and Y() methods
  if (hasMethod(obj, 'X') && hasMethod(obj, 'Y')) {
    const x = (obj as unknown as { X: () => number }).X();
    const y = (obj as unknown as { Y: () => number }).Y();
    if (isFinite(x) && isFinite(y)) {
      return { minX: x, maxX: x, minY: y, maxY: y };
    }
  }

  // Curves/FunctionGraphs - sample points along the curve
  if (hasMethod(obj, 'Y') && hasMethod(obj, 'minX') && hasMethod(obj, 'maxX')) {
    const curve = obj as unknown as {
      minX: () => number;
      maxX: () => number;
      Y: (x: number) => number;
    };
    const xMin = curve.minX();
    const xMax = curve.maxX();

    let minY = Infinity,
      maxY = -Infinity;
    const samples = 50;
    const step = (xMax - xMin) / samples;

    for (let x = xMin; x <= xMax; x += step) {
      const y = curve.Y(x);
      if (isFinite(y)) {
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
      }
    }

    if (isFinite(minY) && isFinite(maxY)) {
      return { minX: xMin, maxX: xMax, minY, maxY };
    }
  }

  // Lines - check for point1 and point2
  if (hasProperty(obj, 'point1') && hasProperty(obj, 'point2')) {
    const line = obj as unknown as {
      point1: { X: () => number; Y: () => number };
      point2: { X: () => number; Y: () => number };
    };
    if (line.point1?.X && line.point2?.X) {
      const x1 = line.point1.X(),
        y1 = line.point1.Y();
      const x2 = line.point2.X(),
        y2 = line.point2.Y();
      if (isFinite(x1) && isFinite(y1) && isFinite(x2) && isFinite(y2)) {
        return {
          minX: Math.min(x1, x2),
          maxX: Math.max(x1, x2),
          minY: Math.min(y1, y2),
          maxY: Math.max(y1, y2),
        };
      }
    }
  }

  return null;
}

function hasMethod(obj: unknown, method: string): boolean {
  return (
    obj !== null &&
    typeof obj === 'object' &&
    method in obj &&
    typeof (obj as Record<string, unknown>)[method] === 'function'
  );
}

function hasProperty(obj: unknown, prop: string): boolean {
  return obj !== null && typeof obj === 'object' && prop in obj;
}
